// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import { findings } from '../analysis';
import { lineChart } from './charts';
import { card, chartBox, findingCards, legend, stats, viewHead } from './ui';
import { gloss } from '../glossary';
import { lastKnown, money, num, period, rate, weeks } from '../format';
import { FATAL, PHYSICAL, PSYCH } from '../colors';
import { totalOf } from '../data';
import type { Bundle } from '../types';

export const title = 'Overview';

export function render(b: Bundle): string {
  const { claims, fatalities } = b;
  const P = claims.periods;
  const n = P.length;
  const total = totalOf(claims.nature)!;
  const mental = claims.nature.find((r) => r.code === 'I')!;

  const compIdx = lastKnown(total.comp ?? [])?.index ?? n - 2;
  const lostIdx = lastKnown(total.lost ?? [])?.index ?? n - 2;
  const recent = fatalities.series[fatalities.series.length - 1];

  const share = P.map((_, i) =>
    total.count[i] && mental.count[i] !== null ? (mental.count[i]! / total.count[i]!) * 100 : null,
  );

  // The two curves, each rebased to 100 at the first year, because they are in
  // different units (deaths per 100,000 workers against a claim share) and the
  // POINT is that they move in opposite directions.
  const fatalWindow = fatalities.series.filter((d) => d.rate !== null);
  const baseRate = fatalWindow[0]?.rate ?? 1;
  const fatalIndexed = fatalWindow.map((d) => (d.rate !== null ? (d.rate / baseRate!) * 100 : null));
  const psychBase = share.find((v) => v !== null) ?? 1;
  // Align the claim series onto the fatality year axis by financial-year start.
  const psychByYear = new Map<number, number | null>();
  P.forEach((p, i) => psychByYear.set(Number(p.slice(0, 4)), share[i]));
  const psychIndexed = fatalWindow.map((d) => {
    const v = psychByYear.get(d.year);
    return v === null || v === undefined ? null : (v / psychBase!) * 100;
  });

  const maxIdx = Math.max(120, ...[...fatalIndexed, ...psychIndexed].filter((v): v is number => v !== null));

  return `
    ${viewHead(
      'Work injuries in Australia',
      'Are Australian workplaces getting safer?',
      `Two things are happening at once, and they point in opposite directions. Far fewer people are being killed by injuries at work than twenty years ago. Far more are being made ill by them — and the illnesses cost several times what the injuries do. Every figure here comes from Safe Work Australia's own annual releases.`,
    )}

    ${stats([
      { value: num(recent.count), label: `Workers killed at work in ${recent.year}`, sub: `${rate(recent.rate, 2)} per 100,000 workers`, tone: 'fatal', tip: `Traumatic injury fatalities, ${recent.year}.\nDeaths from occupational disease are counted separately and are undercounted everywhere.` },
      { value: num(total.count[n - 1]), label: `Serious claims in ${period(P[n - 1])}`, sub: 'preliminary — always revised upward', tip: `A serious claim means a week or more off work.\n${period(P[n - 1])} is preliminary: claims keep being accepted after the year ends.` },
      { value: money(total.comp?.[compIdx] ?? null), label: 'Median claim payment', sub: `the middle claim, ${period(P[compIdx])}`, tone: 'warn', tip: 'Half of all finalised claims paid less than this, half paid more.' },
      { value: money(mental.comp?.[compIdx] ?? null), label: 'Median psychological claim', sub: `${((mental.comp![compIdx]! / total.comp![compIdx]!)).toFixed(2)}× the typical claim`, tone: 'psych', tip: `Median compensation paid on a mental health condition claim, ${period(P[compIdx])}.` },
      { value: weeks(total.lost?.[lostIdx] ?? null), label: 'Median time off work', sub: `any injury, ${period(P[lostIdx])}`, tip: 'Working weeks of compensated absence on the middle claim.' },
      { value: weeks(mental.lost?.[lostIdx] ?? null), label: 'Median time off, psychological', sub: `${(mental.lost![lostIdx]! / total.lost![lostIdx]!).toFixed(2)}× as long`, tone: 'psych' },
    ])}

    ${card(
      'The two curves',
      `Both series rebased to 100 in their first year, because a death rate and a share of claims cannot share an axis. What matters is the direction: one falls by half, the other roughly doubles.`,
      chartBox(
        lineChart({
          series: [
            { values: fatalIndexed, color: FATAL, label: 'Work death rate', width: 2.4 },
            { values: psychIndexed, color: PSYCH, label: 'Psychological share of claims', width: 2.4 },
          ],
          labels: fatalWindow.map((d) => String(d.year)),
          domain: [0, Math.ceil(maxIdx / 20) * 20],
          width: 820,
          height: 300,
          fmtY: (v) => String(v),
          everyX: 2,
          tip: (i) => {
            const y = fatalWindow[i];
            const s = psychByYear.get(y.year);
            return `${y.year}\nWork death rate: ${rate(y.rate, 2)} per 100,000 (index ${fatalIndexed[i] === null ? '—' : fatalIndexed[i]!.toFixed(0)})\nPsychological share: ${s === null || s === undefined ? '—' : `${s.toFixed(2)}% of serious claims`}`;
          },
        }),
        'wide',
      ) +
        legend([
          { color: FATAL, label: 'Work death rate per 100,000 workers' },
          { color: PSYCH, label: 'Psychological injury as a share of serious claims' },
        ]),
    )}

    ${card(
      'What a claim costs, physical against psychological',
      `Median compensation paid, in dollars. The most recent year is withheld on both series — compensation is still accruing on claims that have not been finalised, so ${gloss('Safe Work Australia suppresses it', 'np')} rather than publish a figure that would only fall.`,
      chartBox(
        lineChart({
          series: [
            { values: total.comp ?? [], color: PHYSICAL, label: 'All serious claims', width: 2.4 },
            { values: mental.comp ?? [], color: PSYCH, label: 'Mental health conditions', width: 2.4 },
          ],
          labels: P.map(period),
          domain: [0, Math.ceil((Math.max(...(mental.comp ?? []).filter((v): v is number => v !== null)) * 1.1) / 10000) * 10000],
          width: 820,
          height: 280,
          fmtY: (v) => `$${(v / 1000).toFixed(0)}k`,
          tip: (i) => `${period(P[i])}\nAll serious claims: ${money(total.comp?.[i] ?? null)}\nMental health conditions: ${money(mental.comp?.[i] ?? null)}${total.comp?.[i] === null ? '\n\nWithheld — compensation still accruing' : ''}`,
        }),
        'wide',
      ) +
        legend([
          { color: PHYSICAL, label: 'Median payment, all serious claims' },
          { color: PSYCH, label: 'Median payment, mental health conditions' },
        ]),
    )}

    ${card('What the data shows', `Every finding below is derived from the released figures on each build, with the detection rule stated. None rests on a suppressed value, and none claims a record. Where a finding quotes ${period(P[n - 1])} it is quoting a claim <em>count</em>, which is published for that year but preliminary and certain to rise; the two cost measures are withheld for it entirely.`, findingCards(findings(b)))}
  `;
}
